import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const VERIFY_ERRORS: Record<string, string> = {
  invalid_token: "That verification code isn't valid.",
  token_expired: "That verification code has expired. Request a new one.",
};

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { adoptSessionToken } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function verify(code: string) {
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.verifyEmail(code.trim().toUpperCase());
      // Server returns a session token — adopt it and land on the slate.
      adoptSessionToken(res.token);
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && VERIFY_ERRORS[err.code]
          ? VERIFY_ERRORS[err.code]
          : "Couldn't verify your email. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-submit when a token arrives via the email link. Deferred to a
  // microtask so no setState runs synchronously inside the effect.
  useEffect(() => {
    const linked = params.get("token");
    if (linked) queueMicrotask(() => void verify(linked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="page auth-page">
      <h1>Verify your email</h1>
      <p className="muted">Enter the verification code we emailed you.</p>
      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          void verify(token);
        }}
        noValidate
      >
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        <label className="auth-label" htmlFor="verify-token">
          Verification code
          <input
            id="verify-token"
            inputMode="text"
            autoCapitalize="characters"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Verifying…" : "Verify email"}
        </button>
      </form>
    </section>
  );
}
